import { useCallback } from "react";
import simbApi from "../../utils/requests";
import { analytics } from "../../utils/segment/analyticsService";
import useUser from "../context/userHook";
import logger from "../../lib/logger";

/**
 * Imperative activation-nudge trigger for the "share item" click flows:
 *  - A2 (`share_opened_item`)  — share clicked in an item view
 *  - A3 (`share_opened_board`) — share clicked in a board view
 *
 * Mirrors useNudgeTrigger (which handles A1 on item-view load): emits the
 * `share_flow_opened` analytics event and notifies the backend, which decides
 * the nudge id, suppression and scheduling. Fire-and-forget — a nudge must
 * never affect the user's session. The backend's account-level dedupe is the
 * real guarantee; the sessionStorage guard just avoids redundant calls.
 */
type NudgeSource = "share_opened_item" | "share_opened_board";

interface FireArgs {
  source: NudgeSource;
  itemid?: string | number | null;
  boardid?: string | number | null;
  viewType: "item_view" | "board_view";
}

export const useFireNudgeTrigger = () => {
  const { userId, accountId } = useUser();

  return useCallback(
    ({ source, itemid, boardid, viewType }: FireArgs) => {
      // No target item → nothing to deep-link to (e.g. an empty board). Skip.
      if (!userId || !accountId || !itemid) return;

      const guardKey = `nudge-${source}-${itemid}`;
      if (sessionStorage.getItem(guardKey)) return;
      sessionStorage.setItem(guardKey, "1");

      analytics.track("share_flow_opened", {
        groupId: `${accountId}`,
        board_id: `${boardid ?? ""}`,
        item_id: `${itemid}`,
        view_type: viewType,
      });

      simbApi
        .post("/nudges/trigger", {
          userid: `${userId}`,
          accountid: `${accountId}`,
          boardid: `${boardid ?? ""}`,
          itemid: `${itemid}`,
          source,
        })
        .catch((error) => {
          logger.warn?.("nudge trigger failed", error);
        });
    },
    [userId, accountId],
  );
};

export default useFireNudgeTrigger;
